/**
 * miseEnPage — ce que `set_page_layout` décide hors de l'éditeur : les
 * écritures qu'une section doit recevoir, et ce que la relecture du fichier
 * établit de chaque réglage. Module pur ; `pageLayoutExecutor.ts` parle seul à
 * l'éditeur.
 *
 * ## Ce qui est mesuré (2026-10-06 et 2026-10-07)
 *
 * Sondes `tests/ws-mocked/mesure-socle-page-et-defauts.spec.ts` et
 * `mesure-socle-numero-et-page.spec.ts` (document à deux sections), gabarit
 * Hector, suivi coupé, juge = le fichier exporté ; relevé : §15 de
 * `docs/features/redaction-web-canvas/superdoc-document-api.md`.
 *
 * 1. `sections.list({})` publie par section `pageSetup:{width, height,
 *    orientation}` et `margins:{top, right, bottom, left, gutter}`, en POUCES
 *    (A4 : 8,268 × 11,693 ; marge de 2 cm : 0,7875).
 * 2. `sections.setPageMargins({target, top?, right?, bottom?, left?})`, en
 *    pouces : les côtés passés sont écrits (`w:pgMar`, twips), les autres gardés.
 * 3. `sections.setPageSetup({target, orientation})` écrit `w:orient` ET échange
 *    `w:w` et `w:h`. `{target, width, height}`, en pouces, écrit les dimensions
 *    et garde l'orientation.
 * 4. ⚠️ `setPageSetup({paperSize:'A5'})` n'écrit qu'un code (`w:code="A5"`) : la
 *    page garde ses dimensions, à l'écran comme au fichier. Un format se pose
 *    donc par ses dimensions.
 * 5. ⚠️ Un réglage déjà en place est accepté (`success`) et laisse quand même
 *    une entrée d'historique : ce qui est déjà en place n'est pas réécrit, sans
 *    quoi le compte d'écritures du reçu ferait annuler à l'avocat un geste qui
 *    n'a rien changé.
 * 6. Un document à deux sections publie deux entrées ; chaque écriture ne règle
 *    que la section visée. Une entrée d'historique par écriture, aucune
 *    révision suivie.
 *
 * Non mesuré : `setPageSetup` avec l'orientation ET les dimensions en un appel
 * pour passer en paysage (mesuré seulement pour revenir en portrait) — d'où
 * deux écritures, chacune mesurée.
 */

import {
  CANVAS_FORMAT_READ_SECTIONS_MAX,
  CANVAS_PAGE_LAYOUT_READ_MAX,
  CANVAS_PAGE_MARGIN_SIDES,
  CANVAS_PAGE_TEXT_AREA_CM_MIN,
  type CanvasActionInput,
  type CanvasPageLayoutReceipt,
  type CanvasPageLayoutSettingKey,
  type CanvasPageMarginSide,
  type CanvasPageOrientation,
  type CanvasPageRead,
  type CanvasPaperSize,
} from '@hector/shared-types';
import { cmEnTwips, mmEnTwips, poucesEnTwips, twipsEnCm, twipsEnPouces, type PageDeSection } from '@hector/docx-forme';

/**
 * Les formats de la série A, petit côté puis grand côté, en twips — dérivés de
 * leurs dimensions en millimètres (ISO 216 : A3 297 × 420, A4 210 × 297, A5
 * 148 × 210). L'A4 ainsi calculé, 11906 × 16838, est celui du gabarit Hector.
 */
const FORMATS: Readonly<Record<CanvasPaperSize, readonly [court: number, long: number]>> = {
  A3: [mmEnTwips(297), mmEnTwips(420)],
  A4: [mmEnTwips(210), mmEnTwips(297)],
  A5: [mmEnTwips(148), mmEnTwips(210)],
};

/** Deux mesures en twips qui disent la même chose : l'éditeur et le fichier arrondissent chacun au twip. */
const memeMesure = (lue: number, attendue: number): boolean => Math.abs(lue - attendue) <= 1;

export type DemandeDePage = Pick<Extract<CanvasActionInput, { action: 'set_page_layout' }>, 'marginsCm' | 'orientation' | 'paperSize'>;

/** La page d'une section telle que `sections.list` la publie, en pouces. `null` : non publié. */
export interface SectionPubliee {
  largeurPouces: number | null;
  hauteurPouces: number | null;
  orientation: CanvasPageOrientation | null;
  margesPouces: Record<CanvasPageMarginSide, number | null>;
}

/** Une écriture de l'éditeur sur une section, sans sa cible. */
export type EcritureDePage =
  | { operation: 'setPageMargins'; entree: Partial<Record<CanvasPageMarginSide, number>> }
  | { operation: 'setPageSetup'; entree: { orientation: CanvasPageOrientation } | { width: number; height: number } };

const cotesDemandes = (demande: DemandeDePage): Array<[CanvasPageMarginSide, number]> => CANVAS_PAGE_MARGIN_SIDES.flatMap((cote) => {
  const cm = demande.marginsCm?.[cote];
  return cm === undefined ? [] : [[cote, cm] as [CanvasPageMarginSide, number]];
});

/** L'orientation d'une section avant et après la demande, et si la demande la tourne. `actuelle` nulle : ni publiée ni déductible des dimensions. */
function rotationDemandee(demande: DemandeDePage, section: SectionPubliee): { actuelle: CanvasPageOrientation | null; voulue: CanvasPageOrientation; tournee: boolean } {
  const { largeurPouces: largeur, hauteurPouces: hauteur } = section;
  const actuelle = section.orientation ?? (largeur === null || hauteur === null ? null : largeur > hauteur ? 'landscape' : 'portrait');
  const voulue = demande.orientation ?? actuelle ?? 'portrait';
  return { actuelle, voulue, tournee: demande.orientation !== undefined && actuelle !== voulue };
}

/** Une section où la demande ne laisserait pas de quoi écrire : laquelle, dans quelle dimension, et les mesures en centimètres. */
export interface ZoneDeTexteTropPetite {
  /** Rang de la section dans le document. */
  section: number;
  dimension: 'largeur' | 'hauteur';
  /** La dimension de la page telle que la demande la laisserait. */
  pageCm: number;
  /** Les deux marges qui s'en retranchent : demandées, ou en place. */
  margesCm: Partial<Record<CanvasPageMarginSide, number>>;
  /** Ce qui resterait pour le texte ; négatif quand les marges dépassent la page. */
  resteCm: number;
}

const MARGES_DE_LA_DIMENSION = { largeur: ['left', 'right'], hauteur: ['top', 'bottom'] } as const satisfies Record<ZoneDeTexteTropPetite['dimension'], readonly [CanvasPageMarginSide, CanvasPageMarginSide]>;

/**
 * La première section où la page demandée — ou en place — moins les marges
 * demandées — ou en place — laisserait moins de
 * {@link CANVAS_PAGE_TEXT_AREA_CM_MIN} pour le texte, en largeur ou en hauteur ;
 * `null` : aucune. Vaut pour les marges seules, le format seul (un A5 sur de
 * grandes marges en place) et l'orientation seule (une page tournée change de
 * hauteur).
 *
 * L'éditeur accepte ces pages et les écrit (mesuré le 2026-10-07,
 * `tests/ws-mocked/canvas-mise-en-page.spec.ts` : A5 sur 9 cm de marges gauche
 * et droite, `success`). Une dimension ou une marge que l'éditeur ne publie pas
 * et que la demande ne donne pas n'est pas jugée : rien n'en est supposé.
 */
export function zoneDeTexteTropPetite(demande: DemandeDePage, sections: readonly SectionPubliee[]): ZoneDeTexteTropPetite | null {
  const enTwips = (pouces: number | null): number | null => (pouces === null ? null : Math.round(poucesEnTwips(pouces)));
  for (const [rang, section] of sections.entries()) {
    const { actuelle, voulue, tournee } = rotationDemandee(demande, section);
    const enPlace = { largeur: enTwips(section.largeurPouces), hauteur: enTwips(section.hauteurPouces) };
    const [court, long] = demande.paperSize === undefined ? [null, null] : FORMATS[demande.paperSize];
    const page: Record<ZoneDeTexteTropPetite['dimension'], number | null> = court !== null
      ? (voulue === 'landscape' ? { largeur: long, hauteur: court } : { largeur: court, hauteur: long })
      // L'éditeur échange largeur et hauteur d'une page qu'il tourne (fait mesuré n° 3).
      : (tournee && actuelle !== null ? { largeur: enPlace.hauteur, hauteur: enPlace.largeur } : enPlace);
    for (const dimension of ['largeur', 'hauteur'] as const) {
      const marges = MARGES_DE_LA_DIMENSION[dimension].map((cote) => {
        const cm = demande.marginsCm?.[cote];
        return cm === undefined ? enTwips(section.margesPouces[cote]) : cmEnTwips(cm);
      });
      const [premiere, seconde] = marges;
      const mesure = page[dimension];
      if (mesure === null || premiere === null || premiere === undefined || seconde === null || seconde === undefined) continue;
      const reste = mesure - premiere - seconde;
      if (reste >= cmEnTwips(CANVAS_PAGE_TEXT_AREA_CM_MIN)) continue;
      const [coteA, coteB] = MARGES_DE_LA_DIMENSION[dimension];
      return { section: rang, dimension, pageCm: twipsEnCm(mesure), margesCm: { [coteA]: twipsEnCm(premiere), [coteB]: twipsEnCm(seconde) }, resteCm: twipsEnCm(reste) };
    }
  }
  return null;
}

/**
 * Les écritures qu'une section doit recevoir pour porter la demande, dans
 * l'ordre : marges, orientation, dimensions. Rien pour ce qui est déjà en place
 * (fait mesuré n° 5).
 */
export function ecrituresDePage(demande: DemandeDePage, section: SectionPubliee): EcritureDePage[] {
  const ecritures: EcritureDePage[] = [];

  const cotes = cotesDemandes(demande);
  const dejaEnPlace = cotes.every(([cote, cm]) => {
    const publiee = section.margesPouces[cote];
    return publiee !== null && memeMesure(poucesEnTwips(publiee), cmEnTwips(cm));
  });
  if (cotes.length > 0 && !dejaEnPlace) {
    ecritures.push({ operation: 'setPageMargins', entree: Object.fromEntries(cotes.map(([cote, cm]) => [cote, twipsEnPouces(cmEnTwips(cm))])) });
  }

  const { largeurPouces: largeur, hauteurPouces: hauteur } = section;
  const { actuelle, voulue, tournee } = rotationDemandee(demande, section);
  if (tournee) ecritures.push({ operation: 'setPageSetup', entree: { orientation: voulue } });

  if (demande.paperSize !== undefined) {
    const [court, long] = FORMATS[demande.paperSize];
    const [largeurVoulue, hauteurVoulue] = voulue === 'landscape' ? [long, court] : [court, long];
    // Ce que la page mesure une fois tournée : l'éditeur échange largeur et hauteur (fait mesuré n° 3).
    // Une page dont l'orientation n'était pas publiée n'est pas supposée tournée.
    const [largeurApres, hauteurApres] = tournee && actuelle !== null ? [hauteur, largeur] : [largeur, hauteur];
    const aLaTaille = largeurApres !== null && hauteurApres !== null
      && memeMesure(poucesEnTwips(largeurApres), largeurVoulue) && memeMesure(poucesEnTwips(hauteurApres), hauteurVoulue);
    if (!aLaTaille) ecritures.push({ operation: 'setPageSetup', entree: { width: twipsEnPouces(largeurVoulue), height: twipsEnPouces(hauteurVoulue) } });
  }
  return ecritures;
}

type ReglageDuRecu = CanvasPageLayoutReceipt['settings'][number];
/** Ce qu'une section dit d'un réglage : la valeur lue, et si elle est celle demandée ; `null` : le fichier ne l'écrit pas. */
type LuDansUneSection = { lu: number | string; conforme: boolean } | null;

const CLE_DE_MARGE: Readonly<Record<CanvasPageMarginSide, CanvasPageLayoutSettingKey>> = {
  top: 'marginTopCm', bottom: 'marginBottomCm', left: 'marginLeftCm', right: 'marginRightCm',
};

function reglage(setting: CanvasPageLayoutSettingKey, requested: number | string, sections: readonly PageDeSection[] | null, lire: (section: PageDeSection) => LuDansUneSection): ReglageDuRecu {
  const lus = (sections ?? []).map(lire);
  if (lus.some((lu) => lu !== null && !lu.conforme)) {
    const distinctes = [...new Set(lus.flatMap((lu) => (lu === null ? [] : [lu.lu])))].slice(0, CANVAS_PAGE_LAYOUT_READ_MAX);
    return { setting, requested, outcome: 'different', read: distinctes };
  }
  return { setting, requested, outcome: lus.length === 0 || lus.includes(null) ? 'unreadable' : 'conforming' };
}

/** Le nom du format dont une page a les dimensions, quelle que soit son orientation ; `undefined` : hors de la liste. */
export function nomDuFormat(largeurTwips: number, hauteurTwips: number): CanvasPaperSize | undefined {
  const [court, long] = [Math.min(largeurTwips, hauteurTwips), Math.max(largeurTwips, hauteurTwips)];
  return (Object.keys(FORMATS) as CanvasPaperSize[]).find((nom) => memeMesure(court, FORMATS[nom][0]) && memeMesure(long, FORMATS[nom][1]));
}

/** Le format d'une page par son nom, ou ses dimensions en centimètres quand il n'est pas de la liste. */
function formatLu(court: number, long: number): string {
  return nomDuFormat(court, long) ?? `${twipsEnCm(court)} x ${twipsEnCm(long)} cm`;
}

/**
 * La page de chaque section telle que l'agent la lit par `canvas_inspect`
 * `formatOf` : les unités de `set_page_layout` (centimètres, nom de format),
 * pour lire avant d'écrire.
 */
export function pagePourLAgent(sections: readonly PageDeSection[]): CanvasPageRead[] {
  const cm = (twips: number | null): number | null => (twips === null ? null : twipsEnCm(twips));
  return sections.slice(0, CANVAS_FORMAT_READ_SECTIONS_MAX).map(({ largeurTwips, hauteurTwips, orientation, margesTwips }) => {
    const nom = largeurTwips === null || hauteurTwips === null ? undefined : nomDuFormat(largeurTwips, hauteurTwips);
    return {
      ...(nom === undefined ? {} : { paperSize: nom }),
      widthCm: cm(largeurTwips),
      heightCm: cm(hauteurTwips),
      orientation,
      marginsCm: { top: cm(margesTwips.top), bottom: cm(margesTwips.bottom), left: cm(margesTwips.left), right: cm(margesTwips.right) },
    };
  });
}

/**
 * Juge chaque réglage demandé sur TOUTES les sections du fichier relu :
 * conforme si toutes le portent, différent dès qu'une ne le porte pas (avec les
 * valeurs lues), non lisible si le fichier n'a pas été relu, ne porte aucune
 * section ou n'écrit pas la valeur.
 *
 * L'orientation est jugée aussi sur les dimensions : une page marquée paysage
 * mais plus haute que large n'est pas tournée.
 *
 * @param sections Les sections du fichier exporté (`FormeDeLActe.sections`), ou `null` si l'export a échoué.
 */
export function jugerLaPage(demande: DemandeDePage, sections: readonly PageDeSection[] | null): ReglageDuRecu[] {
  const reglages: ReglageDuRecu[] = cotesDemandes(demande).map(([cote, cm]) => reglage(CLE_DE_MARGE[cote], cm, sections, (section) => {
    const twips = section.margesTwips[cote];
    return twips === null ? null : { lu: twipsEnCm(twips), conforme: memeMesure(twips, cmEnTwips(cm)) };
  }));
  const { orientation, paperSize } = demande;
  if (orientation !== undefined) {
    reglages.push(reglage('orientation', orientation, sections, ({ largeurTwips, hauteurTwips, orientation: ecrite }) => {
      if (largeurTwips === null || hauteurTwips === null) return null;
      const selonLesDimensions: CanvasPageOrientation = largeurTwips > hauteurTwips ? 'landscape' : 'portrait';
      // Dimensions et marque d'accord avec la demande : conforme. Sinon, ce qui la contredit est rendu.
      if (selonLesDimensions !== orientation) return { lu: selonLesDimensions, conforme: false };
      return { lu: ecrite ?? selonLesDimensions, conforme: ecrite === null || ecrite === orientation };
    }));
  }
  if (paperSize !== undefined) {
    const [court, long] = FORMATS[paperSize];
    reglages.push(reglage('paperSize', paperSize, sections, ({ largeurTwips, hauteurTwips }) => {
      if (largeurTwips === null || hauteurTwips === null) return null;
      const [courtLu, longLu] = [Math.min(largeurTwips, hauteurTwips), Math.max(largeurTwips, hauteurTwips)];
      return { lu: formatLu(courtLu, longLu), conforme: memeMesure(courtLu, court) && memeMesure(longLu, long) };
    }));
  }
  return reglages;
}
